import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip/Tooltip';
import { Focusable } from 'react-aria-components';
import { Link, useNavigate } from 'react-router-dom';

import InspectLink from '../../components/InspectLink';
import Table from '../../components/Table';
import { openRowInNewContext } from '../../lib/openRowInNewContext';
import { renderWorkItemBody } from '../../lib/renderWorkItemBody';
import { assistantThreadDeepLinkPath, objectInspectorPath } from '../../paths';
import { WORK_ITEM_CATEGORY_LABEL } from '../WorkItems/workItemsView';

import { Absent, HeldBadge, STACKED_TIMESTAMP_COLUMN_CLASS, Timestamp } from './AccountFields';
import { OUTREACH_CHANNEL_LABELS, groupNotificationsByOutreach } from './accountsView';

import type { AdminGetFamilyAccountQuery } from './__generated__/AccountsScene.gql';

type LinkedUser = NonNullable<AdminGetFamilyAccountQuery['adminGetFamilyAccount']>['linkedUsers'][number];
type Notification = LinkedUser['recentNotifications'][number];

/**
 * Narrower than the item's own: a row is a message already sent, and the hold
 * it reports is the one standing now, which a caregiver may have stamped after.
 */
const NOTIFICATION_HELD_TITLE =
  "Off a loved one's read as things stand now. A caregiver may have stamped the hold after this message went out.";

/** Said in words so a card with no detail is not mistaken for a broken tooltip. */
const NO_BODY_DESCRIPTION = 'This card carries no detail under its heading.';

const CURRENT_CARD_DESCRIPTION =
  'This delivery recorded no card for the item, so this is its card as it reads now rather than as the message carried it.';

interface IRecentNotificationsPanelProps {
  notifications: Notification[];
  /** The recipient, so each thread link opens with their thread list alongside it. */
  userId: string;
}

/**
 * What the assistant reached this member about recently, newest first.
 *
 * Folds under the outreach summary rather than carrying a heading of its own:
 * it is the detail behind the counts directly above it, and a second label
 * would read as a second subject.
 *
 * One block per message, not per row. An outreach bundles several items by
 * design, so a message covering three draws one date, one channel, and one
 * thread against three synopses. The item count therefore runs ahead of the
 * delivered totals above, which count conversations.
 *
 * Each synopsis is the card the delivery recorded, so an item edited or deleted
 * since reads as the message carried it. Where the delivery recorded no card,
 * the item's card as it reads now stands in, marked current; an item with none
 * now shows none, and its row keeps the send on the record.
 */
const RecentNotificationsPanel = ({ notifications, userId }: IRecentNotificationsPanelProps) => {
  const navigate = useNavigate();
  const outreaches = groupNotificationsByOutreach(notifications);

  if (outreaches.length === 0) {
    return <div className="text-fg-tertiary text-xs italic">Nothing reached them in this window.</div>;
  }

  return (
    <Table className="table-fixed">
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell className={STACKED_TIMESTAMP_COLUMN_CLASS}>Date</Table.HeaderCell>
          <Table.HeaderCell className="w-[4.5rem]">Channel</Table.HeaderCell>
          <Table.HeaderCell>Synopsis</Table.HeaderCell>
          <Table.HeaderCell className="w-[6rem]">Category</Table.HeaderCell>
          <Table.HeaderCell className="w-[5.5rem]">Thread</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      {/*
        One `<tbody>` per outreach. A table section is the markup that already
        means "these rows belong together", so the grouping survives a screen
        reader and a text selection rather than living only in the border.
      */}
      {outreaches.map((outreach) => (
        <Table.Body key={outreach.outreachId} className="border-border-primary border-t first:border-t-0">
          {outreach.items.map((notification, index) => (
            <Table.Row
              key={`${notification.outreachId}:${notification.workItemId}`}
              onClick={(event) => openRowInNewContext(event, objectInspectorPath(notification.workItemId), navigate)}
              className="cursor-pointer"
            >
              {/*
                The message's own three columns are written once, against the
                first of its items; the rest of the block leaves them empty so
                the eye reads one send rather than three.
              */}
              <Table.Cell muted className="align-top text-xs">
                {index === 0 && <Timestamp instant={notification.deliveredAt} stacked />}
              </Table.Cell>
              <Table.Cell muted className="align-top text-xs uppercase tracking-wider">
                {index === 0 && (
                  <span className="flex flex-col leading-tight">
                    <span>{OUTREACH_CHANNEL_LABELS[notification.channel]}</span>
                    {/*
                      The blank date and channel cells below already imply one
                      send, but only to a reader who reads them as deliberate
                      rather than as missing. The count says it outright.
                    */}
                    {outreach.items.length > 1 && (
                      <span className="text-fg-quaternary normal-case tracking-normal">
                        {outreach.items.length} items
                      </span>
                    )}
                  </span>
                )}
              </Table.Cell>
              <Table.Cell className="align-top">
                <SynopsisCell notification={notification} to={objectInspectorPath(notification.workItemId)} />
              </Table.Cell>
              <Table.Cell muted className="align-top text-[10px] uppercase tracking-wide">
                {notification.category === null || notification.category === undefined ? (
                  <Absent>None</Absent>
                ) : (
                  WORK_ITEM_CATEGORY_LABEL[notification.category]
                )}
              </Table.Cell>
              {/* The thread is a different destination, so its click stops here. */}
              <Table.Cell className="align-top text-xs" onClick={(event) => event.stopPropagation()}>
                {index === 0 && <ThreadLink threadId={notification.threadId ?? null} userId={userId} />}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      ))}
    </Table>
  );
};

interface ISynopsisCellProps {
  notification: Notification;
  /** Where the card opens: the work item, in the object inspector. */
  to: string;
}

const SynopsisCell = ({ notification, to }: ISynopsisCellProps) => {
  const title = notification.title ?? null;
  // "No card" already reads as the item now, so only a shown synopsis needs the marker.
  const showsCurrentCard = title !== null && !notification.cardAsDelivered;

  return (
    <div className="flex min-w-0 items-baseline gap-2">
      {title === null ? (
        <span className="truncate" onClick={(event) => event.stopPropagation()}>
          <Absent>No card</Absent> <InspectLink id={notification.workItemId} short />
        </span>
      ) : (
        // An anchor rather than the tooltip's own button trigger, so the title
        // can be command-clicked into a new tab and draws the hand cursor.
        // `Focusable` is what lets the tooltip attach to something that is not
        // a button.
        <Tooltip
          title={title}
          description={renderWorkItemBody(notification.body ?? null) ?? NO_BODY_DESCRIPTION}
          placement="top"
        >
          <Focusable>
            {/* The row navigates too, so its handler must not fire a second time. */}
            <Link to={to} onClick={(event) => event.stopPropagation()} className="min-w-0 flex-1 cursor-pointer">
              <span className="block truncate text-left">{title}</span>
            </Link>
          </Focusable>
        </Tooltip>
      )}
      {(showsCurrentCard || notification.caregiverOnly) && (
        <span className="ml-auto flex shrink-0 items-baseline gap-2">
          {showsCurrentCard && <CurrentCardBadge />}
          {notification.caregiverOnly && <HeldBadge description={NOTIFICATION_HELD_TITLE} />}
        </span>
      )}
    </div>
  );
};

/** Marks a synopsis read off the item as it stands now, where the delivery recorded no card. */
const CurrentCardBadge = () => (
  <Tooltip title="Current card" description={CURRENT_CARD_DESCRIPTION} placement="top" wide>
    <TooltipTrigger>
      <span className="text-fg-tertiary shrink-0 text-[10px] uppercase tracking-wide">current</span>
    </TooltipTrigger>
  </Tooltip>
);

interface IThreadLinkProps {
  threadId: string | null;
  userId: string;
}

const ThreadLink = ({ threadId, userId }: IThreadLinkProps) =>
  threadId === null ? (
    <Absent>None</Absent>
  ) : (
    <Link
      to={assistantThreadDeepLinkPath({ userId, threadId })}
      className="text-fg-brand-primary hover:text-fg-brand-secondary font-mono underline-offset-2 hover:underline"
    >
      {threadId.slice(0, 8)}
    </Link>
  );

export default RecentNotificationsPanel;
